import Logo from 'components/Logo';
import Spinner from 'components/Spinner';
import classNames from 'classnames';
import {
  TrackingMap,
  StatusHeader,
  DeliveryStepper,
  StatusBanner,
} from './components';
import {SupportIcon} from './icons';
import {useTracking} from './useTracking';
import styles from './Tracking.module.scss';

type Props = {
  id: string;
};

function Tracking({id}: Props) {
  const {
    trackingDetails,
    isLoading,
    status,
    shouldShowBannerOnly,
    shouldShowMap,
    shouldShowFullUI,
    driverName,
    eta,
    stopsBeforeCount,
    formattedAddress,
  } = useTracking({trackingId: id});

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
        <span className={styles.loadingText}>Loading tracking info...</span>
      </div>
    );
  }

  if (!trackingDetails || !status) {
    return (
      <div className={styles.loadingContainer}>
        <span className={styles.loadingText}>
          Tracking information not found
        </span>
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.withMap]: shouldShowMap,
        [styles.withContentOnly]: shouldShowFullUI && !shouldShowMap,
      })}
    >
      <header className={styles.header}>
        <div className={styles.brand}>
          <div className={styles.logo}>
            <Logo />
          </div>
        </div>
      </header>

      {shouldShowMap &&
        trackingDetails.route &&
        trackingDetails.delivery_address && (
          <div className={styles.mapSection}>
            <TrackingMap
              route={trackingDetails.route}
              deliveryAddress={trackingDetails.delivery_address}
            />
          </div>
        )}

      {shouldShowBannerOnly && (
        <StatusBanner status={status} attempt={trackingDetails.attempt} />
      )}

      {shouldShowFullUI && (
        <div className={styles.contentSection}>
          <StatusHeader
            status={status}
            driverName={driverName}
            eta={eta}
            trackingNumber={trackingDetails.sequential_order_number}
          />

          {shouldShowMap && (
            <DeliveryStepper stopsBeforeCount={stopsBeforeCount} eta={eta} />
          )}

          <div className={styles.orderDetails}>
            <h3 className={styles.sectionTitle}>Order Details</h3>
            <div className={styles.detailsCard}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Delivery Address</span>
                <span className={styles.detailValue}>{formattedAddress}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Items</span>
                <div className={styles.itemsList}>
                  {trackingDetails.items.map((item, index) => (
                    <span key={index} className={styles.item}>
                      {item.name} (x{item.quantity})
                    </span>
                  ))}
                </div>
              </div>
              {status === 'delivered' &&
                trackingDetails.delivery?.signature && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Signature</span>
                    <div className={styles.signatureContainer}>
                      <img
                        src={
                          trackingDetails.delivery.signature.medium ||
                          trackingDetails.delivery.signature.file
                        }
                        alt="Delivery signature"
                        className={styles.signatureImage}
                      />
                      {trackingDetails.delivery.accepters_name && (
                        <span className={styles.accepterName}>
                          Signed by: {trackingDetails.delivery.accepters_name}
                        </span>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>

          <div className={styles.supportSection}>
            <div className={styles.supportIcon}>
              <SupportIcon />
            </div>
            <div className={styles.supportContent}>
              <span className={styles.supportTitle}>Need help?</span>
              <span className={styles.supportText}>
                Contact our support team if you have any questions.
              </span>
            </div>
            <a href="tel:+15551234567" className={styles.supportButton}>
              Contact Support
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default Tracking;
